import { DateTimeInput, Field, NumericInput, RadioGroup, TextInput, TextOutput } from 'Components/Core';
import { NamePrefixModifier, withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { ListFieldSection } from 'Components/Layout';
import { DateTimeStyle } from 'Components/Layout/Grid/types';
import {
  WorksheetBalanceFieldSet,
  WorksheetEquipmentDropdown,
  WorksheetGridFieldSection,
  WorksheetRecordGrid,
} from 'Components/Worksheets/Core';
import { IWorksheetComponent } from 'Components/Worksheets/Core/types';
import { createDryMassEntryOptionSelector } from 'Components/Worksheets/MoistureContentWorksheet';
import { getDryMassGridFieldSet } from 'Components/Worksheets/MoistureContentWorksheet/BsEnIso/gridFieldSets';
import React from 'react';
import { useSelector } from 'react-redux';
import { pipe } from 'rxjs';
import WholePortionAssociatedMoistureContentSection from './WholePortionAssociatedMoistureContentSection';

const WholePortionPretreatmentPreparationSectionBase: React.FC<IWorksheetComponent> = ({ createWorksheetSelector }) => {
  const dryMassEntryOption = useSelector(createDryMassEntryOptionSelector(createWorksheetSelector!));
  const showTimeInOven = dryMassEntryOption !== 'Never';

  const showSingleReadingSection = useSelector(
    pipe(
      createWorksheetSelector!('DryingTray.DryMassReadings'),
      (r) => (r?.length ?? 0) <= 1 && dryMassEntryOption === 'Never'
    )
  );

  return (
    <>
      <NamePrefixModifier appendNamePrefix="Masses">
        <NamePrefixModifier appendNamePrefix="MassMeasurement">
          <ListFieldSection>
            <WorksheetBalanceFieldSet name="Equipment.Balance" />
            <Field input={<WorksheetEquipmentDropdown />} name="Equipment.Oven" equipmentType="oven" />
            <Field input={<NumericInput />} name="TareMass" />
            <Field input={<NumericInput />} name="TareAndWetMass" />
            <Field input={<TextOutput />} name="WetMass" />
          </ListFieldSection>
        </NamePrefixModifier>

        <WholePortionAssociatedMoistureContentSection heading="As Received Water Content" />
      </NamePrefixModifier>

      <NamePrefixModifier appendNamePrefix="Pretreatment">
        <ListFieldSection>
          <Field input={<TextOutput />} name="UntreatedMaterialDryMass" />
          <Field
            input={<RadioGroup />}
            name="Reagent"
            options={['Hydrogen peroxide', 'Hydrochloric acid'].map((x) => ({ label: x, value: x }))}
            freeEntry={true}
            freeEntryLabel="Other"
            freeEntryMaxLength={100}
          />
          <Field
            input={<RadioGroup />}
            name="SeparationMethod"
            options={['Centrifuge', 'Sieve'].map((x) => ({ label: x, value: x }))}
            freeEntry={true}
            freeEntryLabel="Other"
            freeEntryMaxLength={100}
          />
          <NamePrefixModifier appendNamePrefix="MeasuredConstantDryMass">
            <WorksheetBalanceFieldSet name="Equipment.Balance" />
            <Field input={<TextInput />} name="DryingTray.ContainerLabel" />
            <Field input={<NumericInput />} name="DryingTray.ContainerMass" />
            {showTimeInOven && <Field input={<DateTimeInput />} name="DryingTray.DateTimePlacedInOvenLocal" />}
            <WorksheetBalanceFieldSet name="Equipment.DryMassBalance" />
          </NamePrefixModifier>
        </ListFieldSection>
        <NamePrefixModifier appendNamePrefix="MeasuredConstantDryMass">
          {showSingleReadingSection ? (
            <ListFieldSection>
              <Field
                input={<NumericInput />}
                name="DryingTray.DryMassReadings[0].DrySoilAndContainerMass"
                // We do not show the dry mass calculated from the Tare + Dry mass, so we need to show its messages on this field
                additionalMessagePaths={['DryingTray.DryMassReadings[0].DrySoilMass']}
                label="Tare and Material Dry Mass"
              />
            </ListFieldSection>
          ) : (
            <WorksheetGridFieldSection name="DryingTray.DryMassReadings">
              <WorksheetRecordGrid
                name="DryingTray.DryMassReadings"
                orientation="vertical"
                recordProps={{
                  enforceAtLeastOneRecord: true,
                  preventRemove: 'all-but-last',
                  preventCreate: dryMassEntryOption === 'Never',
                  removeConfirm: {
                    confirm: true,
                    confirmTitle: 'Delete Dry Mass Reading',
                    confirmText: 'Are you sure you want to delete the dry mass reading? Data cannot be recovered.',
                    confirmButtonText: 'Delete',
                  },
                }}
                fieldSet={getDryMassGridFieldSet(dryMassEntryOption, DateTimeStyle.Combined)}
              />
            </WorksheetGridFieldSection>
          )}
        </NamePrefixModifier>
        <ListFieldSection>
          <Field input={<TextOutput />} name="PretreatedMaterialDryMass" />
        </ListFieldSection>
      </NamePrefixModifier>
    </>
  );
};
const WholePortionPretreatmentPreparationSection = withInjectedProps(WholePortionPretreatmentPreparationSectionBase);

export { WholePortionPretreatmentPreparationSection };
export default WholePortionPretreatmentPreparationSection;
